import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/axios";
import Logo from "../../components/logo/Logo";
import costsFirstLogo from "../../images/costsfirst-logo.webp";
import "./DemoLanding.css";

// Each entry doubles as a checklist label (visual) and the live-region status
// sentence (screen readers) — one wait, one purpose: showing real setup work
// happening instead of a generic spinner.
const SETUP_STEPS = [
  "Creating your private guest workspace",
  "Loading CostsFirst's calculation engine",
  "Opening your calculator dashboard",
];

// The whole loading experience (bar, checklist, countdown) is paced against
// this single budget, so slowing/speeding it down is a one-number change.
// The redirect to Dashboard is deliberately gated on this full duration
// elapsing (see the two effects below), not on the backend responding —
// a fast backend response does not skip the wait.
const TOTAL_SECONDS = 18;

export default function DemoLanding() {
  const theme = useContext(ThemeContext);
  const navigate = useNavigate();
  const { loginUser } = useAuth();

  const [progress, setProgress] = useState(6);
  const [statusIndex, setStatusIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [error, setError] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const headingRef = useRef(null);

  // Refs (not state) so both effects below can read/write them without
  // being in each other's dependency arrays — one holds the freshest
  // countdown value (state alone would be stale inside the API-response
  // effect's closure, since that effect only re-runs on `attempt`), the
  // other two coordinate the single redirect between "timer finished" and
  // "backend responded", whichever happens last.
  const secondsLeftRef = useRef(TOTAL_SECONDS);
  const readyForRedirectRef = useRef(false);
  const redirectedRef = useRef(false);

  const redirectNow = () => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    setProgress(100);
    setStatusIndex(SETUP_STEPS.length - 1);
    setTimeout(() => navigate("/dashboard", { replace: true }), 350);
  };

  // Drive the bar, the checklist step, and the visible countdown off one
  // shared elapsed-time clock (ticking every 150ms for a smooth, continuous
  // fill instead of a handful of chunky steps) rather than three independently
  // incrementing timers — keeps all three readouts consistent with each
  // other and makes the whole pace a single constant (TOTAL_SECONDS) to tune.
  // Caps at 90% until the timer actually finishes, so the jump to 100% reads
  // as "finishing", not as a bar that was already full and stalled.
  useEffect(() => {
    if (error) return undefined;

    const startedAt = Date.now();

    const tick = () => {
      const elapsedMs = Date.now() - startedAt;
      const frac = Math.min(1, elapsedMs / (TOTAL_SECONDS * 1000));
      const secs = Math.max(0, TOTAL_SECONDS - Math.floor(elapsedMs / 1000));

      setProgress(6 + 84 * frac);
      setSecondsLeft(secs);
      secondsLeftRef.current = secs;
      setStatusIndex(frac < 0.3 ? 0 : frac < 0.7 ? 1 : SETUP_STEPS.length - 1);

      // The full ~30s has elapsed. If the backend already responded
      // successfully in the meantime, redirect now; if not, hold here —
      // the API-response effect below will redirect the moment it lands.
      if (secs <= 0 && readyForRedirectRef.current) {
        redirectNow();
      }
    };

    tick();
    const ticker = setInterval(tick, 150);
    return () => clearInterval(ticker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, attempt]);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    const isRetry = attempt > 0;

    // On a retry, guarantee the loading state stays visible for at least
    // this long, even if the request fails immediately — otherwise a fast
    // repeat failure just swaps the error text for itself with no visible
    // transition, and clicking "Try again" looks like it did nothing.
    const MIN_RETRY_VISIBLE_MS = 700;
    const settle = (fn) => {
      if (cancelled) return;
      const wait = isRetry ? Math.max(0, MIN_RETRY_VISIBLE_MS - (Date.now() - startedAt)) : 0;
      setTimeout(() => {
        if (!cancelled) fn();
      }, wait);
    };

    const startDemoSession = async () => {
      setError(null);
      setIsRateLimited(false);
      setProgress(6);
      setStatusIndex(0);
      setSecondsLeft(TOTAL_SECONDS);
      secondsLeftRef.current = TOTAL_SECONDS;
      readyForRedirectRef.current = false;
      redirectedRef.current = false;

      try {
        const res = await api.post(
          "/auth/demo-session",
          { mainid: theme?.id },
          { skipAuthRedirect: true },
        );

        if (cancelled) return;

        if (res?.data?.status === "success" && res.data.user && res.data.token) {
          loginUser(res.data.user, res.data.token);
          readyForRedirectRef.current = true;
          // Backend already responded before the ~30s pacing finished — do
          // NOT redirect yet. The ticking effect above will call
          // redirectNow() once secondsLeftRef hits 0. Only redirect here,
          // immediately, if that timer has already run out (a slower-than-
          // expected backend response) — there's no reason to wait any
          // longer once we're already past the target duration.
          if (secondsLeftRef.current <= 0) {
            redirectNow();
          }
        } else {
          settle(() => {
            setIsRetrying(false);
            setError(res?.data?.message || "We couldn't start your demo session.");
          });
        }
      } catch (err) {
        // 429 = the rate limiter (src/middleware/rateLimit.js on the
        // backend) — an expected safety mechanism, not something broken.
        // Flagged separately so the UI can give it its own, calmer
        // treatment instead of the generic red "something failed" styling.
        settle(() => {
          setIsRetrying(false);
          setIsRateLimited(err?.response?.status === 429);
          setError(
            err?.response?.data?.message ||
              "We couldn't start your demo session right now.",
          );
        });
      }
    };

    startDemoSession();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Move focus to the heading whenever the visible state changes (initial
  // mount, and again on any loading <-> error transition) so screen-reader
  // users get an immediate, unambiguous announcement of what's on screen now.
  useEffect(() => {
    headingRef.current?.focus();
  }, [error]);

  const handleRetry = () => {
    // Flip this synchronously, in the click handler itself, so the button
    // gives instant feedback regardless of how quickly the effect below
    // actually fires — never dependent on React's own scheduling timing.
    setIsRetrying(true);
    setAttempt((a) => a + 1);
  };

  return (
    <div className="demo-landing-outer">
      <div className="demo-landing-card">
        <div className="demo-landing-logo-row">
          <img
            src={costsFirstLogo}
            alt={theme?.main_company_name || "CostsFirst"}
            className="demo-landing-logo"
          />
        </div>

        {!error ? (
          <>
            <span className="demo-landing-badge">NO SIGNUP REQUIRED</span>
            <p
              className="demo-landing-heading"
              ref={headingRef}
              tabIndex={-1}
            >
              Getting your{" "}
              <span className="demo-landing-heading-accent">demo</span> ready
            </p>
            <p className="demo-landing-subtext">
              We're creating your private, temporary workspace. Try a
              property address to explore the calculators. Closing costs and
              fees shown in this demo are sample estimates.
            </p>

            <div
              className="demo-landing-track"
              role="progressbar"
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Preparing your demo session"
            >
              <div
                className="demo-landing-fill"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <p className="demo-landing-countdown" aria-hidden="true">
              {secondsLeft > 0 ? `About ${secondsLeft}s remaining` : "Almost there…"}
            </p>

            <ul className="demo-landing-checklist" aria-hidden="true">
              {SETUP_STEPS.map((step, i) => {
                const state =
                  i < statusIndex ? "done" : i === statusIndex ? "active" : "pending";
                return (
                  <li key={step} className={`demo-landing-checklist-item ${state}`}>
                    <span className="demo-landing-checklist-indicator">
                      {state === "done" && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                      )}
                    </span>
                    {step}
                  </li>
                );
              })}
            </ul>

            <p className="demo-landing-sr-status" aria-live="polite">
              {SETUP_STEPS[statusIndex]}
            </p>
          </>
        ) : (
          <>
            <p
              className="demo-landing-heading"
              ref={headingRef}
              tabIndex={-1}
            >
              {isRateLimited ? "You're going a bit fast" : "We couldn't start your demo"}
            </p>

            {isRateLimited ? (
              <div className="demo-landing-rate-limit-box" role="alert">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "none" }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
                <p className="demo-landing-rate-limit-text">{error}</p>
              </div>
            ) : (
              <p className="demo-landing-status" role="alert">
                {error}
              </p>
            )}

            <p className="demo-landing-subtext">
              {isRateLimited
                ? "For fair access, guest sessions are limited per visitor. Please wait a few minutes before trying again, or sign in if you already have an account."
                : attempt >= 2
                  ? "Still not resolving — you can keep trying, or sign in below if you already have an account."
                  : "That's on us, not you — try again, or sign in if you already have an account."}
            </p>
            <div className="demo-landing-actions">
              <button
                type="button"
                className="demo-landing-retry-btn"
                onClick={handleRetry}
                disabled={isRetrying}
              >
                {isRetrying ? "Retrying…" : "Try again"}
              </button>
              <a href="/login" className="demo-landing-signin-link">
                Sign in instead
              </a>
            </div>
          </>
        )}
      </div>

      <div className="company-logo top-15">
        <Logo type="secondary" size={140} />
      </div>
    </div>
  );
}
